import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import KibTag from '@chewy/kib-tag-react'
import { type Constant, DateUtils, Utils } from '@pbt/pbt-ui-components'

import type { DashboardLabTestEntry } from '~/api/graphql/generated/types'
import LabActionsCell from '~/components/dashboard/soapV2/summary/labs/LabActionsCell'
import LabNameCell from '~/components/dashboard/soapV2/summary/labs/LabNameCell'
import {
  ColorVariantToTheme,
  LabStatusToColorVariant,
} from '~/constants/colors'
import { useLabTestsSubscription } from '~/hooks/useLabTestsSubscription'
import i18n from '~/locales/i18n'
import { getLabTestsStates } from '~/store/reducers/constants'

import { RIGHT_RAIL_DATE_COL_PX } from '../constants'
import PatientHistoryEmptyState from '../PatientHistoryEmptyState'
import PatientHistoryWidget, { type TabData } from '../PatientHistoryWidget'

import '@chewy/kib-tag-styles/dist/kib-tag.css'
import styles from './LabsHistoryWidget.module.scss'

const labsColumns = (
  clientId: string,
  patientId: string,
  LabTestsStatesList: Constant[],
) => [
  {
    id: 'date',
    fixedWidth: RIGHT_RAIL_DATE_COL_PX,
    prop: (item: DashboardLabTestEntry) => DateUtils.formatDate(item.orderDate),
    label: i18n.t('Common:DATE_TIME'),
    clamp: 1,
    allowRowGrowth: false,
  },
  {
    id: 'labName',
    width: 8,
    component: (item: DashboardLabTestEntry) => <LabNameCell item={item} />,
    label: i18n.t('Common:LAB'),
    allowRowGrowth: true,
  },
  {
    id: 'type',
    width: 4,
    prop: (item: DashboardLabTestEntry) => item.testLogs?.[0]?.labType || '--',
    label: i18n.t('Common:TYPE_ONE'),
    clamp: 1,
    allowRowGrowth: false,
  },
  {
    id: 'status',
    width: 5,
    component: (item: DashboardLabTestEntry) => (
      <div className={styles.statusCell}>
        {item.testLogs?.map((test) => (
          <div className={styles.statusTag} key={test.id}>
            <KibTag
              theme={
                ColorVariantToTheme[
                  LabStatusToColorVariant[
                    Utils.getConstantName(
                      test.statusId || '',
                      LabTestsStatesList,
                    )
                  ]
                ]
              }
            >
              <span className={styles.statusText}>
                {Utils.getConstantName(test.statusId || '', LabTestsStatesList)}
              </span>
            </KibTag>
          </div>
        ))}
      </div>
    ),
    label: i18n.t('Common:STATUS'),
    allowRowGrowth: true,
    disableEllipsis: true,
    isAction: true,
  },
  {
    id: 'actions',
    fixedWidth: 45,
    component: (item: DashboardLabTestEntry) => (
      <LabActionsCell clientId={clientId} item={item} patientId={patientId} />
    ),
    label: '',
    isAction: true,
    allowRowGrowth: false,
  },
]

interface LabsHistoryWidgetProps {
  clientId: string
  patientId: string
}

const COMPLETED_TAB_ID = 0
const IN_PROGRESS_TAB_ID = 1
const RESULTS_RECEIVED_TAB_ID = 2

const LabsHistoryWidget = ({ clientId, patientId }: LabsHistoryWidgetProps) => {
  const { t } = useTranslation(['Clients'])
  const [selectedTab, setSelectedTab] = useState(0)

  const [currentPage, setCurrentPage] = useState(1)

  const {
    completedLabTests,
    inProgressLabTests,
    resultsReceivedLabTests,
    loading: isLoading,
    completedLabTestsCount,
    inProgressLabTestsCount,
    resultsReceivedLabTestsCount,
  } = useLabTestsSubscription(patientId, currentPage - 1)

  const LabTestsStatesList = useSelector(getLabTestsStates)

  const chronologicallySortLabTests = (
    a: DashboardLabTestEntry,
    b: DashboardLabTestEntry,
  ) => {
    const dateA = new Date(a.orderDate)
    const dateB = new Date(b.orderDate)
    return dateB.getTime() - dateA.getTime()
  }

  const completedLabs = useMemo(
    () => [...completedLabTests].sort(chronologicallySortLabTests),
    [completedLabTests],
  )

  const inProgressLabs = useMemo(
    () => [...inProgressLabTests].sort(chronologicallySortLabTests),
    [inProgressLabTests],
  )

  const resultsReceivedLabs = useMemo(
    () => [...resultsReceivedLabTests].sort(chronologicallySortLabTests),
    [resultsReceivedLabTests],
  )

  const columns = useMemo(
    () => labsColumns(clientId, patientId, LabTestsStatesList),
    [clientId, patientId, LabTestsStatesList],
  )

  const tabs: TabData<DashboardLabTestEntry>[] = [
    {
      id: COMPLETED_TAB_ID,
      label: t('Clients:LABS_WIDGET.COMPLETED'),
      labelCount: completedLabTestsCount,
      data: completedLabs,
      columns,
      totalCount: completedLabTestsCount,
      noRecords: (
        <PatientHistoryEmptyState
          message={t('Clients:LABS_WIDGET.NO_COMPLETED_LABS')}
        />
      ),
    },
    {
      id: IN_PROGRESS_TAB_ID,
      label: t('Clients:LABS_WIDGET.IN_PROGRESS'),
      labelCount: inProgressLabTestsCount,
      data: inProgressLabs,
      columns,
      totalCount: inProgressLabTestsCount,
      noRecords: (
        <PatientHistoryEmptyState
          message={t('Clients:LABS_WIDGET.NO_IN_PROGRESS_LABS')}
        />
      ),
    },
    {
      id: RESULTS_RECEIVED_TAB_ID,
      label: t('Clients:LABS_WIDGET.RESULTS_RECEIVED'),
      labelCount: resultsReceivedLabTestsCount,
      data: resultsReceivedLabs,
      columns,
      totalCount: resultsReceivedLabTestsCount,
      noRecords: (
        <PatientHistoryEmptyState
          message={t('Clients:LABS_WIDGET.NO_RESULTS_RECEIVED_LABS')}
        />
      ),
    },
  ]

  return (
    <PatientHistoryWidget
      currentPage={currentPage}
      isLoading={isLoading}
      layoutOptions={{
        flushRows: true,
      }}
      selectedTab={selectedTab}
      tabs={tabs}
      title={t('Clients:LABS_WIDGET.TITLE')}
      onPageChange={setCurrentPage}
      onSelectedTabChange={setSelectedTab}
    />
  )
}

export default LabsHistoryWidget
